(() => {

  if (!customElements.get('fc-sticky-purchase')) {
    customElements.define('fc-sticky-purchase', class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        const root = this.closest('[data-fc-main-product]');
        const form = root?.querySelector('.fc-product-form');
        const add = root?.querySelector('[data-fc-add-to-cart]');
        const price = root?.querySelector('[data-fc-product-price]');
        if (!form || !add || !price) return;
        const button = this.querySelector('[data-fc-sticky-add]');
        const sync = () => {
          const selected = root.querySelector('[data-fc-variant-option]:checked');
          if (selected) this.querySelector('[data-fc-sticky-option]').textContent = selected.dataset.variantTitle;
          this.querySelector('[data-fc-sticky-price]').textContent = price.textContent;
          const quantity = form.querySelector('[name="quantity"]')?.value || '1';
          this.querySelector('[data-fc-sticky-quantity]').textContent = Number(quantity) > 1 ? `× ${quantity}` : '';
          button.disabled = add.disabled;
          button.textContent = add.textContent;
          button.setAttribute('aria-busy', String(add.getAttribute('aria-busy') === 'true'));
        };
        const visibility = () => {
          this.frame = null;
          const header = document.querySelector('header-component');
          const top = Math.max(0, header?.getBoundingClientRect().bottom || 0);
          this.hidden = add.getBoundingClientRect().bottom > top;
        };
        const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(visibility); };
        button.addEventListener('click', () => {
          if (add.disabled) return;
          if (!form.checkValidity()) { form.reportValidity(); return; }
          form.requestSubmit(add);
        }, options);
        root.addEventListener('change', sync, options);
        root.addEventListener('input', sync, options);
        window.addEventListener('scroll', schedule, { ...options, passive: true });
        window.addEventListener('resize', schedule, options);
        this.observer = new MutationObserver(sync);
        this.observer.observe(add, { attributes: true, childList: true, subtree: true });
        this.observer.observe(price, { childList: true, subtree: true, characterData: true });
        sync();
        visibility();
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        cancelAnimationFrame(this.frame);
      }
    });
  }

  const initProduct = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const galleries = [...root.querySelectorAll('[data-fc-pack-gallery]')];
    const showGalleryMedia = (gallery, mediaId) => {
      const panels = [...gallery.querySelectorAll('[data-fc-gallery-stage-media]')];
      const target = panels.find(panel => panel.dataset.mediaId === String(mediaId)) || panels[0];
      panels.forEach(panel => {
        const active = panel === target;
        panel.hidden = !active;
        panel.classList.toggle('is-current', active);
        if (!active) panel.querySelectorAll('video').forEach(video => video.pause());
      });
      gallery.querySelectorAll('[data-fc-gallery-thumb]').forEach(thumb => {
        const active = thumb.dataset.mediaId === target?.dataset.mediaId;
        thumb.classList.toggle('is-current', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
    };
    const showVariantImage = (option) => {
      if (!option) return;
      const addon = option.dataset.addon === 'true';
      const heading = root.querySelector('[data-fc-product-heading]');
      if (heading && option.dataset.heading) heading.textContent = option.dataset.heading;
      root.querySelectorAll('[data-fc-copy]').forEach(copy => {
        copy.hidden = copy.dataset.fcCopy !== (addon ? 'addon' : 'complete');
      });
      galleries.forEach(gallery => {
        gallery.hidden = gallery.dataset.fcPackGallery !== option.value;
        gallery.querySelectorAll('video').forEach(video => video.pause());
        if (!gallery.hidden) showGalleryMedia(gallery);
      });
    };
    galleries.forEach(gallery => {
      gallery.querySelectorAll('[data-fc-gallery-thumb]').forEach(thumb => {
        thumb.addEventListener('click', () => showGalleryMedia(gallery, thumb.dataset.mediaId));
      });
    });
    const variantId = root.querySelector('[data-fc-variant-id]');
    const price = root.querySelector('[data-fc-product-price]');
    const add = root.querySelector('[data-fc-add-to-cart]');
    const included = root.querySelector('[data-fc-edition-included]');
    const includedTitle = root.querySelector('[data-fc-included-title]');
    const includedPanels = [...root.querySelectorAll('[data-fc-included-panel]')];
    const showIncluded = (option, expand = false) => {
      if (!option) return;
      const key = option.dataset.includedKey || '';
      const title = option.dataset.variantTitle || '';
      const prefix = root.dataset.fcIncludedPrefix || 'What’s included in';
      const suffix = root.dataset.fcIncludedSuffix || '';
      if (includedTitle) includedTitle.textContent = `${prefix} ${title}${suffix}`.trim();
      includedPanels.forEach((panel) => { panel.hidden = panel.dataset.fcIncludedPanel !== key; });
      if (included && expand) included.open = true;
    };
    root.querySelectorAll('[data-fc-variant-option]').forEach((option) => {
      const label = option.closest('.fc-edition-option');
      if (label) label.addEventListener('click', () => {
        if (option.checked) showIncluded(option, true);
      });
    });
    root.querySelectorAll('[data-fc-variant-option]').forEach((option) => option.addEventListener('change', () => {
      if (!option.checked) return;
      if (variantId) variantId.value = option.value;
      if (price) price.textContent = option.dataset.price || price.textContent;
      root.querySelectorAll('.fc-edition-option').forEach((label) => label.classList.toggle('is-selected', label.contains(option)));
      const available = option.dataset.available === 'true';
      if (add) {
        add.disabled = !available;
        add.textContent = available ? (add.dataset.addLabel || 'Add to cart') : (add.dataset.soldLabel || 'Sold out');
      }
      showVariantImage(option);
      showIncluded(option, true);
      if (option.dataset.url && window.history?.replaceState) window.history.replaceState({}, '', option.dataset.url);
    }));
    // Keep option buttons, variant data, and gallery artwork synchronized.
    root.querySelectorAll('[data-fc-variant-option]').forEach((option) => {
      const label = option.closest('.fc-edition-option');
      if (!label) return;
      label.addEventListener('click', () => {
        if (option.disabled) return;
        if (!option.checked) {
          option.checked = true;
          option.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          showVariantImage(option);
          showIncluded(option, true);
        }
      });
    });

    const selectedOption = root.querySelector('[data-fc-variant-option]:checked');
    showVariantImage(selectedOption);
    showIncluded(selectedOption);

    const routeInputs = [...root.querySelectorAll('[data-fc-route]')];
    const giftPanel = root.querySelector('[data-fc-gift-panel]');
    const syncPurchaseRoute = () => {
      const selectedRoute = routeInputs.find((input) => input.checked);
      const isGift = selectedRoute?.dataset.fcRoute === 'gift';
      routeInputs.forEach((input) => {
        const label = input.closest('.fc-route-card');
        if (label) label.classList.toggle('is-selected', input === selectedRoute);
        if (input.dataset.fcRoute === 'gift') input.setAttribute('aria-expanded', String(isGift));
      });
      if (giftPanel) {
        giftPanel.hidden = !isGift;
        giftPanel.querySelectorAll('[data-fc-gift-input]').forEach((input) => {
          input.disabled = !isGift;
          if (input.hasAttribute('data-fc-gift-required')) input.required = isGift;
        });
      }
    };
    routeInputs.forEach((input) => input.addEventListener('change', syncPurchaseRoute));
    syncPurchaseRoute();

    const form = root.querySelector('.fc-product-form');
    const cartMessage = root.querySelector('[data-fc-cart-message]');
    if (form) form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!add || add.disabled) return;
      const originalLabel = add.textContent;
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      if (cartMessage) {
        cartMessage.hidden = true;
        cartMessage.classList.remove('is-error');
      }
      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const checkedVariant = root.querySelector('[data-fc-variant-option]:checked');
        if (checkedVariant && variantId) variantId.value = checkedVariant.value;
        const formData = new FormData(form);
        if (checkedVariant) formData.set('id', checkedVariant.value);
        formData.delete('fc-product-option');
        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'This option could not be added to the cart.');
        if (cartMessage) {
          cartMessage.textContent = root.dataset.fcAddedMessage || 'Added to cart. Opening your cart…';
          cartMessage.hidden = false;
        }
        window.location.assign(`${rootPath}cart`);
      } catch (error) {
        if (cartMessage) {
          cartMessage.textContent = error.message || 'This option could not be added. Please try again.';
          cartMessage.classList.add('is-error');
          cartMessage.hidden = false;
          cartMessage.scrollIntoView({ block: 'center' });
        }
      } finally {
        add.disabled = false;
        add.removeAttribute('aria-busy');
        add.textContent = originalLabel;
      }
    });
  };

  const initTabs = (root) => {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const tabs = [...root.querySelectorAll('[data-fc-tab]')];
    const panels = [...root.querySelectorAll('[data-fc-panel]')];
    const activate = (tab) => {
      tabs.forEach((item) => {
        const active = item === tab;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
        item.tabIndex = active ? 0 : -1;
      });
      panels.forEach((panel) => {
        const active = panel.dataset.fcPanel === tab.dataset.fcTab;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        activate(tabs[next]);
        tabs[next].focus();
      });
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-fc-main-product]').forEach(initProduct);
    scope.querySelectorAll('[data-fc-product-tabs]').forEach(initTabs);
  };
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();


